/**
 * Small shared UI atoms for the Company Registration detail sub-components.
 * Extracted from CompanyRegistrationDetailPage.jsx during the iter-238
 * refactor (Feb 2026).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Circle, X, CheckCircle2, Save, Loader2, Download, FileText, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';

export function SectionCard({ icon: Icon, iconColor, title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        {Icon ? <Icon className="h-5 w-5" style={{ color: iconColor || '#3B9EDC' }} /> : null}
        {title}
      </h3>
      {children}
    </div>
  );
}

export function OptionRow({ selected, onSelect, label, strike }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={
        'flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-start ' +
        (selected
          ? 'bg-[#f5a55a]/10 border-[#f5a55a] text-white'
          : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
      }
    >
      <div className={'w-5 h-5 rounded-full border-2 flex items-center justify-center ' + (selected ? 'border-[#f5a55a] bg-[#f5a55a]' : 'border-[#606060]')}>
        {selected ? <Circle className="h-2 w-2 fill-white text-white" /> : null}
      </div>
      <span className={strike ? 'line-through opacity-60' : ''}>
        {strike ? <X className="inline h-3 w-3 me-1" /> : null}
        {label}
      </span>
    </button>
  );
}

export function SumTile({ label, value, color }) {
  return (
    <div className="rounded-xl border p-3" style={{ borderColor: `${color}40`, background: `${color}10` }}>
      <div className="text-[10px] uppercase font-bold tracking-wider mb-1" style={{ color }}>{label}</div>
      <div className="text-white font-mono font-bold">{value}</div>
    </div>
  );
}

export function SummaryCell({ icon: Icon, colour, label, value }) {
  return (
    <div className="rounded-xl bg-[#0a0a0a] border p-3 flex items-center gap-3"
         style={{ borderColor: `${colour}40` }}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center"
           style={{ backgroundColor: `${colour}20`, color: colour }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] text-white/50 uppercase tracking-wider">{label}</div>
        <div className="text-white font-semibold truncate">{value}</div>
      </div>
    </div>
  );
}

export function KV({ k, v, dir }) {
  return (
    <div>
      <div className="text-xs text-[#71717A] mb-1">{k}</div>
      <div className="text-white text-sm truncate" dir={dir || undefined}>{v}</div>
    </div>
  );
}

/**
 * ReceiptDownloadLink — iter-280 (Feb 2026)
 *
 * Shows a compact preview + download button pair for an already-saved
 * receipt (data-URL). Used in every fee box (License / Gov / Admin /
 * Agent / Payments) so operators can retrieve the exact file they
 * uploaded — critical on CLOSED requests where the upload widgets are
 * disabled but the receipt still needs to be pulled up for audit.
 *
 * Props:
 *   url      — the receipt data URL or hosted URL (required)
 *   filename — download filename (optional, falls back to a sane default)
 *   label    — bilingual label (e.g. "إيصال الرخصة")
 *   color    — accent color (hex)
 *   testId   — data-testid prefix (adds -preview / -download)
 */
export function ReceiptDownloadLink({ url, filename, label, color = '#10b981', testId = 'receipt' }) {
  if (!url) return null;
  const safeName = (filename || `${label || 'receipt'}.file`).replace(/\s+/g, '_');
  return (
    <div
      className="mt-2 flex items-center gap-2 p-2 rounded-lg border text-xs"
      style={{ borderColor: `${color}40`, background: `${color}10` }}
    >
      <FileText className="h-3.5 w-3.5" style={{ color }} />
      <span className="flex-1 text-white/80 truncate">{label}</span>
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        className="p-1.5 rounded-md text-white/70 hover:text-white bg-black/30 hover:bg-black/50 border border-white/10 inline-flex items-center"
        data-testid={`${testId}-preview`}
        title="Preview"
      >
        <Eye className="h-3 w-3" />
      </a>
      <a
        href={url}
        download={safeName}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold"
        style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
        data-testid={`${testId}-download`}
      >
        <Download className="h-3 w-3" />
        <span>تحميل</span>
      </a>
    </div>
  );
}

/**
 * SmartSaveButton — iter-264c (Feb 2026)
 *
 * A save button that swaps into a green "✓ تم الحفظ" pill for a moment
 * right after a successful save, then automatically reverts back to
 * "حفظ" the moment the user edits the section again. Per user request:
 * every stage's save button should let the operator see at a glance
 * whether their current changes are persisted or not.
 *
 * Usage:
 *   const dirty = local !== saved;
 *   <SmartSaveButton dirty={dirty} saving={saving} onClick={save} t={t} />
 *
 * The parent controls `dirty` (typically via useMemo comparing local
 * inputs to the last-saved values pulled from `reg`). The component
 * tracks its own "justSaved" flag: whenever `saving` transitions from
 * true→false while `dirty` was true, we assume the save succeeded and
 * flip to the checkmark state; any subsequent `dirty === true` re-arms
 * the Save label.
 */
export function SmartSaveButton({
  dirty,
  saving,
  onClick,
  t,
  color = '#10b981',
  hoverColor = '#059669',
  labelSaveAr = 'حفظ',
  labelSaveEn = 'Save',
  labelSavedAr = 'تم الحفظ',
  labelSavedEn = 'Saved',
  disabled = false,
  className = '',
  ...rest
}) {
  const [justSaved, setJustSaved] = useState(false);
  const wasSaving = useRef(false);

  // Detect the falling edge of `saving` (i.e. the save just finished).
  useEffect(() => {
    if (wasSaving.current && !saving) {
      setJustSaved(true);
    }
    wasSaving.current = saving;
  }, [saving]);

  // Any new edit clears the checkmark so the Save button reappears.
  useEffect(() => {
    if (dirty && justSaved) setJustSaved(false);
  }, [dirty, justSaved]);

  const showCheck = !dirty && justSaved && !saving;

  if (showCheck) {
    return (
      <div
        className={
          'inline-flex items-center gap-2 px-4 py-2 rounded-md border ' +
          'bg-emerald-500/15 border-emerald-500/40 text-emerald-300 text-sm font-semibold ' +
          className
        }
        data-testid={rest['data-testid'] ? `${rest['data-testid']}-saved` : undefined}
      >
        <CheckCircle2 className="h-4 w-4" />
        {t ? t(labelSavedAr, labelSavedEn) : labelSavedEn}
      </div>
    );
  }

  return (
    <Button
      onClick={onClick}
      disabled={disabled || saving || !dirty}
      className={'text-white gap-2 ' + className}
      style={{ backgroundColor: color, '--hover': hoverColor }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverColor; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = color; }}
      {...rest}
    >
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
      {t ? t(labelSaveAr, labelSaveEn) : labelSaveEn}
    </Button>
  );
}
